import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PinwheelModalProps } from "@pinwheel/react-modal";
import { useObjectMemo, modules } from "byzantine";
import type Account from "byzantine/src/Account";
import { useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { formAdapters } from "../../src";
import { useTriggerGuideForceReset } from "../../src/modules/guides/hooks";
import AccountContext from "../contexts/AccountContext";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";

const LOCAL_STORAGE_PDF_KEY = "direct_deposit_pdf";
const FORM_FILE_NAME = "direct_deposit_form.pdf";

export const useDirectDepositFeatureStatus = () => {
  const features = useUserFeatures();
  return useObjectMemo({
    enabled: !!features.direct_deposit,
    ssoProvider: features.direct_deposit,
  });
};

export const useCopyableText = () => {
  const [isRecentlyCopied, setIsRecentlyCopied] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const onCopyButtonClick = useCallback(
    (text: string | undefined | null) => {
      if (!text) {
        return;
      }
      navigator.clipboard.writeText(text);
      setIsRecentlyCopied(true);

      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      timeoutId.current = setTimeout(() => {
        setIsRecentlyCopied(false);
      }, 1000);
    },
    [navigator]
  );

  const copyButtonLabel = isRecentlyCopied ? "Copied!" : "Copy";

  return useObjectMemo({ onCopyButtonClick, copyButtonLabel });
};

export const useDirectDepositContainer = () => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { enabled, ssoProvider } = useDirectDepositFeatureStatus();
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const { fetchSSOToken, isSSOTokenLoading } =
    modules.directDeposit.useGetDirectDepositSSOToken();
  const ssoToken = useRef<string>("");
  const isSsoComplete = useRef<boolean>(false);
  const [isSsoModalOpen, setIsSsoModalOpen] = useState<boolean>(false);
  const [isManualFormOpen, setIsManualFormOpen] = useState<boolean>(false);

  const getToken = useCallback(async () => {
    fetchSSOToken({
      onData({ token }) {
        ssoToken.current = token;
      },
    });
  }, [fetchSSOToken]);

  // Fetch an SSO token once on mount
  useEffect(() => {
    if (enabled && ssoProvider !== "none") {
      getToken();
    }
  }, [enabled, ssoProvider]);

  const ssoResetModalHandler = useCallback(() => {
    getToken();
    setIsSsoModalOpen(false);
    if (isSsoComplete.current) {
      sendNotification({
        type: "success",
        text: l10n.getString(
          "direct-deposit-sso-success-toast",
          {},
          "Direct deposit complete."
        ),
      });
      triggerGuideForceReset();
      isSsoComplete.current = false;
    }
  }, [getToken, l10n, sendNotification, isSsoComplete, triggerGuideForceReset]);

  const ssoToggleModalHandler = () => {
    setIsSsoModalOpen((prev) => !prev);
  };

  const manualToggleFormHandler = () => {
    setIsManualFormOpen((prev) => !prev);
  };

  const pinwheelEventHandlers: PinwheelModalProps = useMemo(
    () => ({
      open: isSsoModalOpen,
      linkToken: ssoToken.current || "",
      onSuccess: () => {
        isSsoComplete.current = true;
      },
      onExit: ssoResetModalHandler,
    }),
    [isSsoModalOpen, ssoToken.current, isSsoComplete, ssoResetModalHandler]
  );

  return useObjectMemo({
    isSSOTokenLoading,
    ssoResetModalHandler,
    ssoToggleModalHandler,
    isManualFormOpen,
    manualToggleFormHandler,
    pinwheelEventHandlers,
  });
};

export const useDirectDepositManual = (dismissForm = () => {}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const { fetchManualEnrollmentDocument, loading: isSubmitFormLoading } =
    modules.directDeposit.useGetDirectDepositManualEnrollmentDocument();
  const { form } = modules.directDeposit.useDirectDepositManualForm();
  const isFormComplete = useRef<boolean>(false);
  const { internalAccounts } = useContext(AccountContext) as {
    internalAccounts: Account[];
  };
  const { direct_deposit_eligible_product_types: eligibleProductTypes } =
    useInstitutionSettings();

  const { submitForm, setFieldValue, values: formValues, resetForm } = form;
  const { accountId, allocationMethod: selectedAllocationMethod } =
    formValues || {};
  const eligibleAccounts = useMemo(
    () =>
      internalAccounts.filter((account) =>
        eligibleProductTypes?.includes(account.product_type)
      ),
    [internalAccounts, eligibleProductTypes]
  );
  const selectedAccount = useMemo(
    () => eligibleAccounts.find((a) => a.id === accountId),
    [accountId]
  );

  // Pre-populate the account selector with the first account available
  useEffect(() => {
    if (!accountId && eligibleAccounts.length) {
      setFieldValue("accountId", eligibleAccounts[0].id);
    }
  }, [eligibleAccounts, setFieldValue]);

  /**
   * Queries the enrollment endpoint and caches the base64 pdf string for
   * the user to optionally download.
   */
  const submitFormHandler = useCallback(async () => {
    const { success } = await submitForm();

    if (success) {
      fetchManualEnrollmentDocument(formValues, {
        onData({ data }) {
          if (!data || !data.startsWith("data:application/pdf;base64")) {
            throw new Error("Error getting direct deposit enrollment document");
          }
          try {
            localStorage.setItem(LOCAL_STORAGE_PDF_KEY, data || "");
            isFormComplete.current = true;
            triggerGuideForceReset();
          } catch (error) {
            throw new Error("Error persisting pdf to local storage");
          }
        },
        messaging: {
          toast: {
            error: l10n.getString(
              "direct-deposit-manual-success-submit-error",
              {},
              "Unable to generate your form. Please try again later."
            ),
          },
        },
      });
    }
  }, [submitForm, fetchManualEnrollmentDocument, formValues, l10n, triggerGuideForceReset]);

  /**
   * Makes the cached base64 data string available to the browser, navigates
   * to the resources to perform the download, and then removes the link from the body.
   */
  const downloadFormHandler = useCallback(() => {
    const base64DataString = localStorage.getItem(LOCAL_STORAGE_PDF_KEY) || "";
    const tempLink = document.createElement("a");
    tempLink.href = base64DataString;

    tempLink.setAttribute("download", FORM_FILE_NAME);
    document.body.appendChild(tempLink);
    tempLink.click();

    tempLink.parentNode?.removeChild(tempLink);

    sendNotification({
      type: "success",
      text: l10n.getString(
        "direct-deposit-manual-success-download-success",
        {},
        "Direct Deposit form downloaded."
      ),
    });

    localStorage.removeItem(LOCAL_STORAGE_PDF_KEY);
    dismissForm();
    resetForm();
    isFormComplete.current = false;
  }, [sendNotification, l10n, dismissForm, resetForm, isFormComplete]);

  return useObjectMemo({
    form,
    submitFormHandler,
    downloadFormHandler,
    isSubmitFormLoading,
    selectedAccount,
    selectedAllocationMethod,
    eligibleAccounts,
    accountFieldFormProps: formAdapters.select(form, "accountId"),
    isFormComplete: isFormComplete.current,
  });
};
